<template>
  <div class="filter">
    <div class="searchbar-filter">
      <input
        id="search"
        v-model="messageSearch"
        class="message-search-bar"
        :placeholder="t('result_view.journal_analysis.searchbar_placeholder')"
      />
    </div>
    <div class="date-filter">
      <VueDatePicker
        v-model="dateRange"
        range
        :format="'yyyy-MM-dd'"
        :enable-time-picker="false"
        :dark="isDarkTheme"
        :placeholder="t('result_view.journal_analysis.datepicker_placeholder')"
        class="date-range-calendar"
      />
    </div>
  </div>
  <div v-if="jouNonMainEntries.length === 0">
    <div v-if="dataStore.oneFileSelected" class="no-data-screen">
      {{ t('result_view.journal_analysis.no_errors_selected') }}
    </div>
    <div v-else class="no-data-screen">
      {{ t('result_view.journal_analysis.no_errors') }}
    </div>
  </div>
  <div v-else>
    <TreeTable
      :value="filteredTree"
      :loading="loading"
      @toggle="onToggle"
      :expandedRows="expandedKeys"
      sort-mode="single"
      @sort="onSort"
      sort-field="D"
      :sort-order="-1"
      removable-sort
      scrollable
      class="error-table"
    >
      <Column
        expander
        field="errorCode"
        :header="t('result_view.journal_analysis.errorcode')"
        filter
        class="entry-left-edge jou-list-entry-item"
      >
        <template #body="{ node }">
          <span>{{ node.data.errorCode }}</span>
          <span
            v-if="!node.children || node.children.length === 0"
            class="info-tooltip-button"
            @click.stop="onInfoClick(node)"
          >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
        </span>
        </template>
      </Column>
      <Column
        field="count"
        :header="t('result_view.journal_analysis.amount')"
        sortable
        class="jou-list-entry-item"
      />
      <Column
        field="TL_TT_TN"
        header="TL | TT | TN"
        class="jou-list-entry-item"
      />
      <Column
        field="SQ"
        :header="t('result_view.journal_analysis.sq')"
        sortable
        filter
        class="jou-list-entry-item"
      />
      <Column
        field="displayDate"
        :header="t('result_view.journal_analysis.date_time')"
        sortable
        filter
        class="jou-list-entry-item"
      />
      <Column
        field="Value"
        :header="t('result_view.journal_analysis.message')"
        class="entry-right-edge jou-list-entry-item"
        style="word-wrap: break-word;"
      />
    </TreeTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useDataStore } from '@/stores/dataStore.js'
import { useI18n } from 'vue-i18n'
import TreeTable from 'primevue/treetable'
import Column from 'primevue/column'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

const { t } = useI18n()
const dataStore = useDataStore()
const loading = ref(false)
const expandedKeys = ref({})
const dateRange = ref(null)
const sortField = ref('D')
const sortOrder = ref(-1)
const messageSearch = ref('')

const theme = ref(localStorage.getItem('user-theme') || 'light-theme')
const isDarkTheme = computed(() => theme.value === 'dark-theme')

// Build tree data with filtering and sorting
const filteredTree = computed(() => {
  return Object.entries(dataStore.jouErrors || {}).map(([errorCode, entries]) => {
    const filteredChildren = entries
      .map((detail, i) => {
        let displayDate = ''
        if (detail.D) {
          const d = new Date(detail.D)
          if (!isNaN(d)) {
            const pad = n => n.toString().padStart(2, '0')
            displayDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
          }
        }
        return {
          key: `${errorCode}-${i}`,
          data: {
            errorCode: '',
            TL_TT_TN: detail.TL_TT_TN || '',
            SQ: detail.SQ || '',
            D: detail.D || '',
            displayDate,
            count: '',
            Value: detail.Value || '',
            Tra: detail.Tra || '',
            zebra: i % 2 === 1
          }
        }
      })
      .filter(child => isDateInRange(child.data.D)) // This will filter out children without D
      // .filter(child => !child.date.D || isDateInRange(child.date.D)) // This will include children without D but doesnt work
      .filter(child => 
        !messageSearch.value ||
        child.data.Value.toLowerCase().includes(messageSearch.value.toLowerCase())
      )
      .sort((a, b) => {
        if (['D', 'SQ'].includes(sortField.value)) {
          const aValue = a.data[sortField.value] || ''
          const bValue = b.data[sortField.value] || ''
          if (aValue === bValue) return 0
          return (aValue > bValue ? 1 : -1) * sortOrder.value
        }
        return 0
      })

    return {
      key: errorCode,
      data: {
        errorCode,
        TL_TT_TN: '',
        SQ: '',
        D: '',
        count: `${filteredChildren.length} Errors`,
        Value: ''
      },
      children: filteredChildren
    }
  }).filter(parent => parent.children.length > 0)
})

const onSort = e => {
  // If Date column is being "unsorted", force descending
  if (e.sortField === 'D' && e.sortOrder === 0) {
    sortField.value = 'D'
    sortOrder.value = -1
  } else {
    sortField.value = e.sortField
    sortOrder.value = e.sortOrder
  }
}

const onToggle = e => {
  expandedKeys.value = e.value
}

const emit = defineEmits(['open-modal'])
const onInfoClick = (node) => {
  emit('open-modal', node.data)
}

const generateErrorList = (errorData) => {
  let array = []
  for (let key in errorData) {
    array.push({ errorCode: key, count: errorData[key].length, main: true })
    for (let detailEntry of errorData[key]) {
      array.push({ ...detailEntry, errorCode: key })
    }
  }
  return array
}

const generateNonMainEntries = (entries) => {
  let result = []
  for (let i = 0; i < entries.length; i++) {
    let entry = entries[i]
    result.push(entry)
  }
  return result
}

onMounted(() => {
  // This sets the sorting of Date by default to descending order and not on removable sorts default state
  onSort({ sortField: 'D', sortOrder: -1 })

  window.addEventListener('theme-changed', syncTheme)
  window.addEventListener('storage', syncTheme)
})

onUnmounted(() => {
  window.removeEventListener('theme-changed', syncTheme)
  window.removeEventListener('storage', syncTheme)
})

function syncTheme() {
  theme.value = localStorage.getItem('user-theme') || 'light-theme'
}

const getJouErrors = computed(() => generateErrorList(dataStore.jouErrors))
const jouNonMainEntries = computed(() => generateNonMainEntries(getJouErrors.value))

// Helper: Parse "YYYY-MM-DD, ..." to Date
function parseDateFromTimestamp(ts) {
  if (!ts) return null
  const date = new Date(ts)
  return isNaN(date) ? null : date
}

// Helper: Check if date is in range
function isDateInRange(ts) {
  const entryDate = parseDateFromTimestamp(ts)
  if (!entryDate) return false
  if (!dateRange.value) return true
  const [start, end] = dateRange.value
  if (start instanceof Date && !end) {
    return entryDate.toDateString() === start.toDateString()
  }
  if (start instanceof Date && end instanceof Date) {
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    return entryDate >= s && entryDate <= e
  }
  return true
}
</script>

<style>
.no-data-screen {
  font-size: 20px;
  text-align: center;
  margin-top: 20px;
}

.error-table-header {
  width: 100%;
  display: flex;
  padding: 20px 20px 0 20px;
}

.table-header-content {
  width: 100%;
  display: flex;
  gap: 15px;
  padding: 0 20px;
}

.table-header-item .entry-left-edge {
  text-align: left;
  width: 10%;
}

.table-header-item {
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
  width: 10%;
  color: var(--text-primary-color);
  text-align: left;
  white-space: nowrap;
}

.error-group {
  display: grid;
  padding: 5px 20px;
}

.jou-list-entry {
  display: none;
  border-radius: 4px;
  gap: 15px;
  font-size: 15px;
  font-weight: 400;
  padding: 5px 20px;
}

.jou-list-entry.show {
  display: flex;
}

.jou-list-entry-main {
  font-size: 15px;
  font-weight: 400;
  line-height: 42px;
  background: var(--list-red-background-color);
  border-radius: 4px;
  cursor: pointer;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.jou-list-entry-item {
  width: 10%;
}

.filter {
  display: flex;
  gap: 10px;
  padding: 25px 25px 0 25px;
}

.date-filter {
  display: flex;
  width: 300px;
  border-radius: 6px;
}

.dp__input {
  border-radius: 6px !important;
}
.dp__input_focus {
  border-color: var(--searchbar-focus-border) !important;
}

.message-search-bar {
  background: var(--searchbar-bg);
  color: var(--searchbar-text);
  border: 1px solid var(--searchbar-border);
  border-radius: 6px;
  height: 38px;
  width: 300px;
  padding: 10px;
  font-size: 1rem;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.message-search-bar::placeholder {
  color: var(--searchbar-placeholder-color);
}

.message-search-bar:hover {
  border-color: var(--searchbar-border-hover);
}

.message-search-bar:focus {
  outline: none;
  border-color: var(--searchbar-focus-border);
  background: var(--searchbar-bg);
  color: var(--searchbar-text);
}

.entry-left-edge {
  border-radius: 4px 0px 0px 4px;
  min-width: 130px;
}

.entry-right-edge {
  border-radius: 0px 4px 4px 0px;
}

.light-grey-background {
  background-color: var(--light-grey-background-color);
}
</style>